// src/components/settings/RepRoster.tsx
//
// Settings → Rep roster: the org admin's field-force screen (Stories 1.2 +
// 1.3). A three-step CSV import (upload → import runs → report), a roster
// table, and per-rep editing: correct name/email/phones/leaf-region, and
// deactivate/reactivate a rep (there is no delete path — history is retained;
// the active flag is the only lifecycle). All data access goes through
// useReps → repsService; region names resolve through useRegions. No
// optimistic updates — the table re-renders from the server's answer only.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Upload, Users, Pencil, Plus, X, MailWarning } from 'lucide-react'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { useReps } from '../../hooks/useReps'
import { useRegions } from '../../hooks/useRegions'
import type { YakRep } from '../../types/yak'
import {
  parseAndValidate,
  formatPhoneUs,
  type CandidateRep,
  type ImportReport,
  type ImportRowRejection,
} from '../../utils/repImport'

// The per-rep edit form's working copy. Phones are held as display strings
// (one per input row) and normalized on save by the service. `active` is not
// here — it changes only through deactivate/reactivate.
interface EditDraft {
  rep: YakRep
  name: string
  email: string
  phones: string[]
  region_id: string
}

type Stage =
  | { kind: 'idle' }
  | {
      kind: 'parsed'
      fileName: string
      candidates: CandidateRep[]
      preRejections: ImportRowRejection[]
    }
  | { kind: 'importing' }
  | { kind: 'report'; report: ImportReport }

export function RepRoster() {
  const { reps, loading, error, importCsv, update, deactivate, reactivate } =
    useReps()
  const { regions, loading: regionsLoading } = useRegions()

  const [stage, setStage] = useState<Stage>({ kind: 'idle' })
  const [fileError, setFileError] = useState<string | null>(null)

  // Per-rep editing (Story 1.3).
  const [editing, setEditing] = useState<EditDraft | null>(null)
  // Deactivate/reactivate confirmation; nextActive is the state we'd move to.
  const [statusTarget, setStatusTarget] = useState<{
    rep: YakRep
    nextActive: boolean
  } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [showInactive, setShowInactive] = useState(true)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const reportHeadingRef = useRef<HTMLHeadingElement | null>(null)
  // Focus returns here when an overlay's trigger row has unmounted (e.g. a
  // filtered-out rep) — WCAG 2.2 AA floor, the Story 1.1 review pattern.
  const rosterHeadingRef = useRef<HTMLHeadingElement | null>(null)
  const overlayTriggerRef = useRef<HTMLElement | null>(null)
  // The Button primitive doesn't forward refs, but it spreads props onto its
  // native <button>, so we focus the import trigger by id on dismiss.
  const UPLOAD_TRIGGER_ID = 'rep-upload-trigger'

  const restoreFocus = (trigger: HTMLElement | null) => {
    if (trigger && document.contains(trigger)) {
      trigger.focus()
    } else {
      rosterHeadingRef.current?.focus()
    }
  }

  // Region id → name, so the roster table can render names for reps.
  const regionNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of regions) map.set(r.id, r.name)
    return map
  }, [regions])

  // A leaf is a region that is no other region's parent (the RegionManagement
  // parentIds derivation). Reps attach to leaves only (the DB leaf guard
  // enforces it); the picker offers leaves plus the rep's current region even
  // if it has since become interior (Story 1.3 decision #3), so an unrelated
  // edit never forces a region move.
  const leafRegions = useMemo(() => {
    const parentIds = new Set(
      regions
        .map(r => r.parent_region_id)
        .filter((id): id is string => id !== null)
    )
    return regions
      .filter(r => !parentIds.has(r.id))
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [regions])

  const regionOptions = useMemo(() => {
    if (!editing) return []
    const opts = leafRegions.slice()
    const originalId = editing.rep.region_id
    if (!opts.some(r => r.id === originalId)) {
      const current = regions.find(r => r.id === originalId)
      if (current) opts.unshift(current)
    }
    return opts
  }, [editing, leafRegions, regions])

  const hasInactive = useMemo(() => reps.some(r => !r.active), [reps])
  const visibleReps = useMemo(
    () => (showInactive ? reps : reps.filter(r => r.active)),
    [reps, showInactive]
  )

  const runMutation = async (fn: () => Promise<void>, onDone: () => void) => {
    setPending(true)
    setActionError(null)
    try {
      await fn()
      onDone()
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Something went wrong'
      )
    } finally {
      setPending(false)
    }
  }

  const openEdit = (rep: YakRep, e: React.MouseEvent<HTMLElement>) => {
    overlayTriggerRef.current = e.currentTarget
    setActionError(null)
    setEditing({
      rep,
      name: rep.name,
      email: rep.email,
      // Show the friendly format; the service re-normalizes on save.
      phones: rep.phones.length > 0 ? rep.phones.map(formatPhoneUs) : [''],
      region_id: rep.region_id,
    })
  }

  const closeEdit = () => {
    const trigger = overlayTriggerRef.current
    overlayTriggerRef.current = null
    setEditing(null)
    setActionError(null)
    restoreFocus(trigger)
  }

  const submitEdit = () => {
    if (!editing || pending) return
    const phones = editing.phones.map(p => p.trim()).filter(p => p.length > 0)
    runMutation(
      () =>
        update(editing.rep.id, {
          name: editing.name,
          email: editing.email,
          phones,
          region_id: editing.region_id,
          // So the service writes region_id only on a real change — keeping an
          // unchanged, now-interior region must not re-trip the leaf guard.
          currentRegionId: editing.rep.region_id,
        }),
      closeEdit
    )
  }

  const setPhone = (i: number, value: string) =>
    setEditing(d =>
      d
        ? { ...d, phones: d.phones.map((p, idx) => (idx === i ? value : p)) }
        : d
    )
  const addPhone = () =>
    setEditing(d => (d ? { ...d, phones: [...d.phones, ''] } : d))
  const removePhone = (i: number) =>
    setEditing(d =>
      d && d.phones.length > 1
        ? { ...d, phones: d.phones.filter((_, idx) => idx !== i) }
        : d
    )

  const editHasPhone = editing?.phones.some(p => p.trim().length > 0) ?? false
  const canSaveEdit =
    !!editing &&
    editing.name.trim().length > 0 &&
    editing.email.trim().length > 0 &&
    editHasPhone

  const openStatus = (
    rep: YakRep,
    nextActive: boolean,
    e: React.MouseEvent<HTMLElement>
  ) => {
    overlayTriggerRef.current = e.currentTarget
    setActionError(null)
    setStatusTarget({ rep, nextActive })
  }

  const closeStatus = () => {
    const trigger = overlayTriggerRef.current
    overlayTriggerRef.current = null
    setStatusTarget(null)
    setActionError(null)
    restoreFocus(trigger)
  }

  const confirmStatus = () => {
    if (!statusTarget || pending) return
    const { rep, nextActive } = statusTarget
    runMutation(
      () => (nextActive ? reactivate(rep.id) : deactivate(rep.id)),
      closeStatus
    )
  }

  // Move focus to the report heading when it appears (keyboard/SR users land
  // on the result, not back at the top of the page).
  useEffect(() => {
    if (stage.kind === 'report') reportHeadingRef.current?.focus()
  }, [stage.kind])

  const openFilePicker = () => {
    setFileError(null)
    fileInputRef.current?.click()
  }

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset so re-selecting the same file fires onChange again.
    event.target.value = ''
    if (!file) return

    setFileError(null)
    let text: string
    try {
      text = await file.text()
    } catch {
      setFileError('That file could not be read. Try saving it again as CSV.')
      return
    }

    const result = parseAndValidate(text, regions)
    if (!result.ok) {
      setStage({ kind: 'idle' })
      setFileError(result.error)
      return
    }
    if (result.candidates.length === 0 && result.rejections.length === 0) {
      setStage({ kind: 'idle' })
      setFileError(
        'That file has a header but no rep rows. Add one row per rep and re-upload.'
      )
      return
    }
    setStage({
      kind: 'parsed',
      fileName: file.name,
      candidates: result.candidates,
      preRejections: result.rejections,
    })
  }

  const runImport = async (
    candidates: CandidateRep[],
    preRejections: ImportRowRejection[]
  ) => {
    setFileError(null)
    setStage({ kind: 'importing' })
    try {
      const serverReport = await importCsv(candidates)
      // Merge client-side rejections (never reached the DB) with DB-side
      // rejections into one report, ordered by CSV line.
      const rejections = [...preRejections, ...serverReport.rejections].sort(
        (a, b) => a.line - b.line
      )
      setStage({
        kind: 'report',
        report: {
          accepted: serverReport.accepted,
          rejected: rejections.length,
          rejections,
        },
      })
    } catch (err) {
      // The import itself failed to run (e.g. not linked to an org). Keep the
      // parsed selection so the admin can retry.
      setStage({
        kind: 'parsed',
        fileName: 'selected file',
        candidates,
        preRejections,
      })
      setFileError(
        err instanceof Error ? err.message : 'The import could not be run.'
      )
    }
  }

  const dismissReport = () => {
    setStage({ kind: 'idle' })
    setFileError(null)
    document.getElementById(UPLOAD_TRIGGER_ID)?.focus()
  }

  const importInFlight = stage.kind === 'importing'

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="md:hidden mb-4 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg px-4 py-3 text-sm">
        The rep roster works best on a larger screen.
      </div>

      <div className="mb-4">
        <h2
          ref={rosterHeadingRef}
          tabIndex={-1}
          className="text-xl font-bold text-gray-900 focus:outline-none"
        >
          Rep roster
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Register your field force so reps are recognized when they call and
          reachable at their email. Upload a CSV with columns name, phones,
          email, region.
        </p>
      </div>

      {/* Import area */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          onChange={handleFile}
        />

        <div className="flex items-center gap-3">
          <Button
            id={UPLOAD_TRIGGER_ID}
            onClick={openFilePicker}
            disabled={importInFlight || regionsLoading}
            aria-describedby={fileError ? 'rep-import-error' : undefined}
          >
            <Upload className="w-4 h-4 mr-1.5" />
            Upload CSV
          </Button>
          {regionsLoading && (
            <span className="text-sm text-gray-500">Loading regions…</span>
          )}
          {stage.kind === 'parsed' && (
            <span className="text-sm text-gray-700">
              {stage.candidates.length}{' '}
              {stage.candidates.length === 1 ? 'row' : 'rows'} ready to import
              {stage.preRejections.length > 0 &&
                ` · ${stage.preRejections.length} will be skipped`}
            </span>
          )}
        </div>

        {fileError && (
          <div
            id="rep-import-error"
            role="alert"
            className="mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
          >
            {fileError}
          </div>
        )}

        {stage.kind === 'parsed' && (
          <div className="mt-4 flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => runImport(stage.candidates, stage.preRejections)}
            >
              Import
            </Button>
            <Button variant="ghost" onClick={() => setStage({ kind: 'idle' })}>
              Cancel
            </Button>
          </div>
        )}

        {importInFlight && (
          <div className="mt-4">
            <Button variant="outline" loading disabled>
              Importing…
            </Button>
          </div>
        )}
      </div>

      {/* Import report */}
      {stage.kind === 'report' && (
        <div
          aria-live="polite"
          className="border border-gray-200 rounded-lg p-4 mb-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <h3
                ref={reportHeadingRef}
                tabIndex={-1}
                className="text-lg font-semibold text-gray-900 focus:outline-none"
              >
                Import complete
              </h3>
              <p className="text-sm text-gray-700 mt-1">
                {stage.report.accepted} accepted, {stage.report.rejected}{' '}
                rejected.
              </p>
            </div>
            <Button variant="ghost" onClick={dismissReport}>
              Dismiss
            </Button>
          </div>

          {stage.report.rejections.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1.5 pr-4 font-medium">Line</th>
                    <th className="py-1.5 pr-4 font-medium">Email</th>
                    <th className="py-1.5 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stage.report.rejections.map(r => (
                    <tr key={`${r.line}-${r.email ?? ''}`}>
                      <td className="py-1.5 pr-4 text-gray-500 align-top">
                        {r.line}
                      </td>
                      <td className="py-1.5 pr-4 text-gray-900 align-top">
                        {r.email ?? '—'}
                      </td>
                      <td className="py-1.5 text-gray-700">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Roster table */}
      {loading ? (
        <div className="space-y-3" aria-hidden="true">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {error}
        </div>
      ) : reps.length === 0 ? (
        <div className="text-center py-10">
          <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">
            Upload a CSV to register your field force.
          </p>
          <Button onClick={openFilePicker} disabled={regionsLoading}>
            <Upload className="w-4 h-4 mr-1.5" />
            Upload CSV
          </Button>
        </div>
      ) : (
        <>
          {hasInactive && (
            <div className="flex items-center justify-end mb-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  checked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)}
                />
                Show inactive reps
              </label>
            </div>
          )}
          {visibleReps.length === 0 ? (
            // Every rep is inactive and "Show inactive reps" is unchecked —
            // point back at the filter rather than render an empty table.
            <div className="text-center py-10">
              <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">
                No active reps to show. Check{' '}
                <span className="font-medium">Show inactive reps</span> to see
                deactivated reps.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Phones</th>
                    <th className="py-2 pr-4 font-medium">Region</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleReps.map(rep => (
                    <tr key={rep.id} className="group">
                      <td className="py-2 pr-4 text-gray-900">{rep.name}</td>
                      <td className="py-2 pr-4 text-gray-700">{rep.email}</td>
                      <td className="py-2 pr-4 text-gray-700">
                        {rep.phones.map(formatPhoneUs).join(', ')}
                      </td>
                      <td className="py-2 pr-4 text-gray-700">
                        {regionNames.get(rep.region_id) ?? '—'}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {rep.active ? (
                            <Badge variant="green">Active</Badge>
                          ) : (
                            <Badge variant="gray">Inactive</Badge>
                          )}
                          {rep.email_bounced_at && (
                            // Story 4.8: the FR-41 bounce flag. Orange advisory
                            // (DESIGN.md); clears when the email is corrected
                            // through the existing editor (the DB trigger owns it).
                            <span
                              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                              title="Email is bouncing — correct the address to clear this flag."
                            >
                              <MailWarning className="w-3 h-3" />
                              Email bouncing
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={e => openEdit(rep, e)}
                            disabled={regionsLoading}
                            title={
                              regionsLoading
                                ? 'Loading regions — editing is available in a moment.'
                                : undefined
                            }
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>
                          {rep.active ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={e => openStatus(rep, false, e)}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={e => openStatus(rep, true, e)}
                            >
                              Reactivate
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Per-rep edit form */}
      {editing && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => {
            // Don't allow Escape to dismiss mid-write — it would unmount the
            // overlay before the result lands and swallow a failed save
            // (matches the Cancel button, which is disabled while pending).
            if (e.key === 'Escape' && !pending) closeEdit()
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-rep-title"
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <h3
              id="edit-rep-title"
              className="text-lg font-semibold text-gray-900 mb-4"
            >
              Edit rep
            </h3>

            {actionError && (
              <div
                id="edit-rep-error"
                role="alert"
                className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
              >
                {actionError}
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-1">
                <label
                  htmlFor="edit-rep-name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  id="edit-rep-name"
                  autoFocus
                  aria-describedby={actionError ? 'edit-rep-error' : undefined}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                  value={editing.name}
                  onChange={e =>
                    setEditing(d => (d ? { ...d, name: e.target.value } : d))
                  }
                />
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="edit-rep-email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  id="edit-rep-email"
                  type="email"
                  aria-describedby={actionError ? 'edit-rep-error' : undefined}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                  value={editing.email}
                  onChange={e =>
                    setEditing(d => (d ? { ...d, email: e.target.value } : d))
                  }
                />
              </div>

              <div className="space-y-1">
                <span className="block text-sm font-medium text-gray-700">
                  Phones
                </span>
                <div className="space-y-2">
                  {editing.phones.map((phone, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        aria-label={`Phone ${i + 1}`}
                        aria-describedby={
                          actionError ? 'edit-rep-error' : undefined
                        }
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                        placeholder="(555) 812-4409"
                        value={phone}
                        onChange={e => setPhone(i, e.target.value)}
                      />
                      {editing.phones.length > 1 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Remove phone ${i + 1}`}
                          onClick={() => removePhone(i)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="ghost" onClick={addPhone}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add phone
                </Button>
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="edit-rep-region"
                  className="block text-sm font-medium text-gray-700"
                >
                  Region
                </label>
                <select
                  id="edit-rep-region"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                  value={editing.region_id}
                  onChange={e =>
                    setEditing(d =>
                      d ? { ...d, region_id: e.target.value } : d
                    )
                  }
                >
                  {regionOptions.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              {!canSaveEdit && (
                // Disabled controls must say why (EXPERIENCE.md#State Patterns).
                <p className="text-xs text-gray-500 mr-auto">
                  Enter a name, an email, and at least one phone to save.
                </p>
              )}
              <Button variant="ghost" onClick={closeEdit} disabled={pending}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={submitEdit}
                loading={pending}
                disabled={!canSaveEdit}
                title={
                  !canSaveEdit
                    ? 'Enter a name, an email, and at least one phone to save.'
                    : undefined
                }
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate / reactivate confirmation */}
      {statusTarget && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => {
            // Same as the edit overlay: no Escape-dismiss while the
            // activation write is in flight (Cancel is disabled then too).
            if (e.key === 'Escape' && !pending) closeStatus()
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="status-rep-title"
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6"
          >
            <h3
              id="status-rep-title"
              className="text-lg font-semibold text-gray-900 mb-1"
            >
              {statusTarget.nextActive ? 'Reactivate' : 'Deactivate'} “
              {statusTarget.rep.name}”
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {statusTarget.nextActive
                ? 'They’ll be recognized again when they call, subject to the same duplicate-phone check as on import.'
                : 'They’ll be treated as an unrecognized caller. Their record and history are kept, and you can reactivate them later.'}
            </p>
            {actionError && (
              <div
                role="alert"
                className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
              >
                {actionError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                autoFocus
                variant="ghost"
                onClick={closeStatus}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={confirmStatus}
                loading={pending}
              >
                {statusTarget.nextActive ? 'Reactivate rep' : 'Deactivate rep'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
